/**
 * https-enforcer.interceptor.ts
 * ------------------------------
 * Angular HTTP interceptor that blocks any outgoing request
 * that is not using HTTPS in production.
 *
 * FERPA/COPPA: No PII may be transmitted over unencrypted HTTP.
 * All API calls must use HTTPS. WebSocket connections must use WSS.
 *
 * Register in your app.config.ts (standalone) or AppModule:
 *
 *   // app.config.ts (standalone)
 *   import { provideHttpClient, withInterceptors } from '@angular/common/http';
 *   import { httpsEnforcerInterceptor } from './core/interceptors/https-enforcer.interceptor';
 *
 *   export const appConfig: ApplicationConfig = {
 *     providers: [
 *       provideHttpClient(withInterceptors([httpsEnforcerInterceptor])),
 *     ],
 *   };
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { throwError } from 'rxjs';

//// might need later `ng generate environments
// import { environment } from '../../../environments/environment';

// ---------------------------------------------------------------------------

/**
 * Functional interceptor (Angular 17+ style).
 *
 * In production: throws an error and blocks the request if the URL is HTTP.
 * In development: logs a warning but allows the request through (localhost).
 */
export const httpsEnforcerInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const url = req.url;

  // Skip relative URLs and non-http schemes
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return next(req);
  }

  const isProduction = window.location.protocol === 'https:';
  const isHttp = url.startsWith('http://');

  if (isHttp) {
    if (isProduction) {
      console.error(`[HttpsEnforcer] Blocked HTTP request in production: ${url}.`);
      return throwError(() => new Error(`HTTPS required. HTTP request to ${url} was blocked.`));
    } else {
      console.warn(`[HttpsEnforcer] HTTP request detected (allowed in dev): ${url}`);
    }
  }

  return next(req);
};

// ---------------------------------------------------------------------------
// Class-based version (for NgModule / older Angular style)
// ---------------------------------------------------------------------------

// import { Injectable } from '@angular/core';
// import {
//   HttpInterceptor, HttpRequest, HttpHandler, HttpEvent
// } from '@angular/common/http';
// import { Observable, throwError } from 'rxjs';
//
// @Injectable()
// export class HttpsEnforcerInterceptor implements HttpInterceptor {
//   intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
//     if (req.url.startsWith('http://') && environment.production) {
//       return throwError(() => new Error(`HTTPS required: ${req.url}`));
//     }
//     return next.handle(req);
//   }
// }
